import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Utensils, Coffee, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { getDifficultyLabel } from '../../utils/constants';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_MEAL = {
  name: '', description: '', meal_type: 'breakfast', calories: '', protein: '', carbs: '', fat: '',
  ingredients: '', instructions: '', prep_time_minutes: '',
};

const EMPTY_PLAN = {
  name: '', description: '', goal: 'general_fitness', difficulty: 'moderate',
  daily_calories: '', daily_protein: '', daily_carbs: '', daily_fat: '', duration_days: '7',
};

const MEAL_TYPE_BADGE = {
  breakfast: 'bg-orange-500/20 text-orange-400',
  lunch: 'bg-yellow-500/20 text-yellow-400',
  dinner: 'bg-blue-500/20 text-blue-400',
  snack: 'bg-green-500/20 text-green-400',
};

// ---------------- Sub-modals ----------------

const MealModal = ({ open, form, setForm, onCancel, onSave }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={onCancel}
      >
        <motion.div
          initial={{ scale: 0.95 }} animate={{ scale: 1 }}
          className="bg-card border border-border p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Add New Meal</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black border-border text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Meal Type</label>
                <select
                  value={form.meal_type}
                  onChange={(e) => setForm({ ...form, meal_type: e.target.value })}
                  className="w-full bg-black border border-border text-white p-2"
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snack">Snack</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Calories</label>
                <Input type="number" value={form.calories} onChange={(e) => setForm({ ...form, calories: e.target.value })} className="bg-black border-border text-white" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {['protein', 'carbs', 'fat'].map((m) => (
                <div key={m}>
                  <label className="text-sm text-muted-foreground">{m[0].toUpperCase() + m.slice(1)} (g)</label>
                  <Input type="number" value={form[m]} onChange={(e) => setForm({ ...form, [m]: e.target.value })} className="bg-black border-border text-white" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Ingredients (comma separated)</label>
              <Input value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} placeholder="chicken, rice, broccoli" className="bg-black border-border text-white" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Instructions</label>
              <Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="bg-black border-border text-white" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
              <Button onClick={onSave} className="flex-1 bg-primary text-black">Save Meal</Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const PlanModal = ({ open, form, setForm, onCancel, onSave }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={onCancel}
      >
        <motion.div
          initial={{ scale: 0.95 }} animate={{ scale: 1 }}
          className="bg-card border border-border p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Create Meal Plan</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Plan Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black border-border text-white" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black border-border text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Goal</label>
                <select value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="w-full bg-black border border-border text-white p-2">
                  <option value="weight_loss">Weight Loss</option>
                  <option value="muscle_building">Muscle Building</option>
                  <option value="endurance">Endurance</option>
                  <option value="general_fitness">General Fitness</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Difficulty</label>
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="w-full bg-black border border-border text-white p-2">
                  <option value="easy">Easy</option>
                  <option value="moderate">Moderate</option>
                  <option value="strict">Strict</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Daily Calories</label>
                <Input type="number" value={form.daily_calories} onChange={(e) => setForm({ ...form, daily_calories: e.target.value })} className="bg-black border-border text-white" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Duration (days)</label>
                <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className="bg-black border-border text-white" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {['daily_protein', 'daily_carbs', 'daily_fat'].map((m) => (
                <div key={m}>
                  <label className="text-sm text-muted-foreground">{m.replace('daily_', '').replace(/^./, (c) => c.toUpperCase())} (g)</label>
                  <Input type="number" value={form[m]} onChange={(e) => setForm({ ...form, [m]: e.target.value })} className="bg-black border-border text-white" />
                </div>
              ))}
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
              <Button onClick={onSave} className="flex-1 bg-primary text-black">Create Plan</Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ---------------- Main tab ----------------

export const MealPlansTab = ({ api, token }) => {
  const [meals, setMeals] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('plans');
  const [showMealModal, setShowMealModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [mealForm, setMealForm] = useState(EMPTY_MEAL);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mealsRes, plansRes] = await Promise.all([api().get('/admin/meals'), api().get('/meal-plans')]);
      setMeals(mealsRes.data.meals || []);
      setMealPlans(plansRes.data.meal_plans || []);
    } catch {
      toast.error('Failed to load meal data');
    }
    setLoading(false);
  };

  const postFormDataAdminEndpoint = async (path, form, fallbackErrMsg, successMsg, resetForm) => {
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => { if (value) formData.append(key, value); });
      await fetch(`${API}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: formData,
      });
      toast.success(successMsg);
      resetForm();
      fetchData();
    } catch {
      toast.error(fallbackErrMsg);
    }
  };

  const saveMeal = () => postFormDataAdminEndpoint('/api/admin/meals', mealForm, 'Failed to save meal', 'Meal saved!', () => {
    setShowMealModal(false);
    setMealForm(EMPTY_MEAL);
  });

  const savePlan = () => postFormDataAdminEndpoint('/api/admin/meal-plans', planForm, 'Failed to save meal plan', 'Meal plan saved!', () => {
    setShowPlanModal(false);
    setPlanForm(EMPTY_PLAN);
  });

  const seedMealPlans = async () => {
    try {
      await api().post('/meal-plans/seed');
      toast.success('Sample meal plans created!');
      fetchData();
    } catch {
      toast.error('Failed to seed meal plans');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeSubTab === 'plans' ? 'default' : 'outline'}
          onClick={() => setActiveSubTab('plans')}
          className={activeSubTab === 'plans' ? 'bg-primary text-black' : ''}
        >
          Meal Plans ({mealPlans.length})
        </Button>
        <Button
          variant={activeSubTab === 'meals' ? 'default' : 'outline'}
          onClick={() => setActiveSubTab('meals')}
          className={activeSubTab === 'meals' ? 'bg-primary text-black' : ''}
        >
          Meals Library ({meals.length})
        </Button>
        {mealPlans.length === 0 && (
          <Button variant="outline" onClick={seedMealPlans}>Generate Sample Plans</Button>
        )}
      </div>

      {activeSubTab === 'plans' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-heading text-xl text-white uppercase tracking-wider">Meal Plans</h3>
            <Button onClick={() => setShowPlanModal(true)} className="bg-primary text-black">
              <Plus className="h-4 w-4 mr-2" /> New Plan
            </Button>
          </div>

          {mealPlans.length === 0 ? (
            <div className="bg-card border border-border p-8 text-center">
              <Utensils className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No meal plans yet. Create one or generate samples.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mealPlans.map((plan) => (
                <div key={plan.id} className="bg-card border border-border p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-heading text-lg text-white uppercase">{plan.name}</h4>
                    <Badge className="bg-primary/20 text-primary">{plan.goal?.replace('_', ' ')}</Badge>
                  </div>
                  <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{plan.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div className="bg-white/5 p-2 text-center">
                      <span className="text-white font-bold">{plan.daily_calories}</span>
                      <span className="text-muted-foreground ml-1">cal</span>
                    </div>
                    <div className="bg-white/5 p-2 text-center">
                      <span className="text-white font-bold">{plan.daily_protein}g</span>
                      <span className="text-muted-foreground ml-1">protein</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline">{plan.duration_days} days</Badge>
                    <Badge variant="outline">{getDifficultyLabel(plan.difficulty)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'meals' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-heading text-xl text-white uppercase tracking-wider">Meals Library</h3>
            <Button onClick={() => setShowMealModal(true)} className="bg-primary text-black">
              <Plus className="h-4 w-4 mr-2" /> New Meal
            </Button>
          </div>

          {meals.length === 0 ? (
            <div className="bg-card border border-border p-8 text-center">
              <Coffee className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No meals yet. Create one to add to plans.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {meals.map((meal) => (
                <div key={meal.id} className="bg-card border border-border p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-white">{meal.name}</h4>
                    <Badge className={MEAL_TYPE_BADGE[meal.meal_type] || MEAL_TYPE_BADGE.snack}>{meal.meal_type}</Badge>
                  </div>
                  <div className="flex gap-4 text-sm text-muted-foreground mb-2">
                    <span>{meal.calories} cal</span>
                    <span>P: {meal.protein}g</span>
                    <span>C: {meal.carbs}g</span>
                    <span>F: {meal.fat}g</span>
                  </div>
                  {meal.ingredients?.length > 0 && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{meal.ingredients.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <MealModal
        open={showMealModal}
        form={mealForm}
        setForm={setMealForm}
        onCancel={() => setShowMealModal(false)}
        onSave={saveMeal}
      />
      <PlanModal
        open={showPlanModal}
        form={planForm}
        setForm={setPlanForm}
        onCancel={() => setShowPlanModal(false)}
        onSave={savePlan}
      />
    </motion.div>
  );
};
